// The arithmetic and the wording behind the credit meter (see components/CreditMeter.tsx).
//
// Kept apart from the component because every interesting decision here is a judgement about what is
// TRUE of an account, and those are worth testing without a DOM: when a balance counts as low, how fast
// it is going, and — most of all — what we are entitled to claim about it.
//
// One constraint runs through the whole file. A balance in this product is the plan's grant minus the
// account's ENTIRE ledger (see netlify/functions/lib/credits.ts): nothing re-grants credits monthly, so
// the meter must never promise that credits come back. Where the design asked for "resets Oct 1" this
// reports the subscription's renewal date instead, which is a fact we actually have.

import { PLAN_DISPLAY_NAMES, tierFromPlanKey } from './stripe-config'

/** What the meter is shouting, if anything. Drives colour, not copy. */
export type CreditMeterLevel = 'healthy' | 'low' | 'out'

/** A balance this small is "low" however big the plan is: two explorations from empty. */
export const LOW_CREDIT_FLOOR = 5
/** …and so is this share of the plan's grant, which is what makes a 500-credit plan warn in time. */
export const LOW_CREDIT_SHARE = 0.2
/** Days of history the burn rate averages over. Long enough to survive a quiet weekend. */
export const BURN_RATE_WINDOW_DAYS = 14
/** Past this, "you'll run dry in N days" is noise rather than warning. */
const MAX_USEFUL_RUNWAY_DAYS = 60

export function creditMeterLevel(remaining: number, allotment: number | null): CreditMeterLevel {
  if (remaining <= 0) return 'out'
  const share = allotment && allotment > 0 ? allotment * LOW_CREDIT_SHARE : 0
  return remaining <= Math.max(LOW_CREDIT_FLOOR, share) ? 'low' : 'healthy'
}

/** Credits as a bare number: whole where whole, one decimal for the half-credit journey runs. */
export function formatCreditCount(credits: number): string {
  const rounded = Math.round(credits * 10) / 10
  return Number.isInteger(rounded) ? rounded.toLocaleString() : rounded.toFixed(1)
}

/**
 * Average credits per day over the trailing window.
 *
 * Averaged across the whole window rather than across active days only: the question the panel answers
 * is "how long will this last at the rate I'm going", and idle days are part of that rate. Returns null
 * when there is no spend to extrapolate from, which is the signal to say nothing at all.
 */
export function burnRatePerDay(
  days: { day: string; net_credits: number }[],
  window: number = BURN_RATE_WINDOW_DAYS,
): number | null {
  if (days.length === 0) return null
  const recent = days.slice(-window)
  const spent = recent.reduce((sum, entry) => sum + Math.max(0, entry.net_credits), 0)
  if (spent <= 0) return null
  return spent / recent.length
}

/** Days until the balance hits zero at the current rate, or null when that isn't worth saying. */
export function daysUntilEmpty(remaining: number, burnPerDay: number | null): number | null {
  if (!burnPerDay || burnPerDay <= 0 || remaining <= 0) return null
  const days = Math.floor(remaining / burnPerDay)
  return days > MAX_USEFUL_RUNWAY_DAYS ? null : Math.max(1, days)
}

export function burnRateLabel(burnPerDay: number | null): string | null {
  if (!burnPerDay) return null
  return `~${formatCreditCount(burnPerDay)} credits / day`
}

export function runwayLabel(remaining: number, burnPerDay: number | null): string | null {
  if (remaining <= 0) return 'no credits left'
  const days = daysUntilEmpty(remaining, burnPerDay)
  if (days === null) return null
  return `dry in about ${days} ${days === 1 ? 'day' : 'days'}`
}

/**
 * The line under the balance: which plan this is, and when it renews.
 *
 * "Renews" rather than "resets" on purpose. The subscription does renew on that date; the credits do
 * not come back on it, and a free account has no date at all — so a free plan states its allowance
 * instead of inventing a cycle. `renewsAt` is only ever the viewer's OWN subscription, which is why a
 * teammate looking at the owner's balance gets the plan name alone.
 */
export function planLine({
  planKey,
  renewsAt,
  allotment,
}: {
  planKey: string | null | undefined
  renewsAt: number | null
  allotment: number | null
}): string {
  const planName = PLAN_DISPLAY_NAMES[tierFromPlanKey(planKey)] ?? 'Current'
  if (renewsAt) {
    const on = new Date(renewsAt * 1000)
    const date = on.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    const days = Math.max(0, Math.ceil((on.getTime() - Date.now()) / 86_400_000))
    return `${planName} plan · renews ${date} · ${days} ${days === 1 ? 'day' : 'days'}`
  }
  if (allotment && allotment > 0) return `${planName} plan · ${formatCreditCount(allotment)} credits included`
  return `${planName} plan`
}

/**
 * What a credit buys. Stated in both units because the two task kinds cost different amounts
 * (TASK_CREDIT_COST = 1, JOURNEY_TASK_CREDIT_COST = 0.5), and "one journey" alone is off by half.
 */
export const CREDIT_EQUIVALENCE = '1 exploration or 2 journeys'

export type CreditMeterAction = 'plans' | 'topup' | 'contact'

/** A pack as the catalog offers it (see /api/stripe-credit-packs). */
export interface CreditPackOffer {
  priceId: string
  credits: number
  amountCents: number
  currency: string
}

/**
 * The pack to offer when someone needs credits now: the smallest one, since this is a stopgap between
 * here and their renewal, not a plan change. Nothing on sale means no pack to offer, and the CTA falls
 * back to a conversation rather than a button that goes nowhere.
 */
export function preferredPack(packs: CreditPackOffer[] | undefined): CreditPackOffer | null {
  if (!packs || packs.length === 0) return null
  return packs.reduce((smallest, pack) => (pack.credits < smallest.credits ? pack : smallest))
}

/** "50 credits for $30" — what the button will actually charge, said before it is pressed. */
export function packOfferLabel(pack: CreditPackOffer): string {
  const amount = (pack.amountCents / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: pack.currency.toUpperCase(),
    minimumFractionDigits: pack.amountCents % 100 === 0 ? 0 : 2,
  })
  return `${formatCreditCount(pack.credits)} credits for ${amount}`
}

/**
 * The call to action for a tier, per the spec: Free is sold a plan, Starter is sold Pro, and Pro — with
 * nothing above it that self-serves — is sold more credits. Enterprise and the deployment tiers land in
 * the same place as Pro, since no plan page helps someone who is already above the catalog.
 */
export function meterCta(
  planKey: string | null | undefined,
  { topUpAvailable }: { topUpAvailable: boolean },
): { label: string; action: CreditMeterAction } {
  const tier = tierFromPlanKey(planKey)
  if (!tier || tier === 'free') return { label: 'View plans', action: 'plans' }
  if (tier === 'individual') return { label: 'Upgrade to Pro', action: 'plans' }
  return topUpAvailable
    ? { label: 'Top off credits', action: 'topup' }
    : { label: 'Talk to us about credits', action: 'contact' }
}

/** The pill's own text. Short at a glance; the panel carries the detail. */
export function pillLabel({
  level,
  remaining,
  allotment,
  waived,
}: {
  level: CreditMeterLevel
  remaining: number
  allotment: number | null
  waived: boolean
}): string {
  if (waived) return 'Credits waived'
  if (level === 'out') return 'Out of credits'
  if (level === 'low') return `${formatCreditCount(remaining)} credits left`
  if (allotment && allotment > 0) return `${formatCreditCount(remaining)} of ${formatCreditCount(allotment)} credits`
  return `${formatCreditCount(remaining)} credits`
}

/** How full the bar is. Null when there is no allotment to be a share of. */
export function meterFillPercent(remaining: number, allotment: number | null): number | null {
  if (!allotment || allotment <= 0) return null
  return Math.max(0, Math.min(100, (remaining / allotment) * 100))
}
